import { HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/outline';
import { HeartIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { CARD } from '../../../components/detail/detail-classes';
import ImageWithFallback from '../../../components/ImageWithFallback';
import { DONATION_TONE } from '../../../constants/donation.const';
import { useParticipatingMerchants } from '../../../hooks/donations/useDonations';
import type { ParticipatingMerchant } from '../../../types/donation.types';
import { nameInitials, shortNameList } from '../../../utils/donation-format';

/** The most merchants the strip shows; the backend sends five at most. */
const MAX_MERCHANTS = 5;

/**
 * Where giving happens. Nothing here links on: the donor gives at a store's checkout, and the
 * receipt comes back to this page.
 */
const STEPS = [
  {
    title: 'Pay with AyeFace',
    body: 'At a participating store, the way you always do.'
  },
  {
    title: 'Add a donation at checkout',
    body: "Round up your bill or pick an amount. It's always your choice."
  },
  {
    title: 'It goes directly to the charity',
    body: "Your receipt shows up here, including an official tax receipt when the charity is approved for tax deduction and you're eligible."
  }
] as const;

/**
 * A few merchants where the donor can give, as overlapping logos with their names, so "a store
 * that supports a charity" has faces. Example only: absent when there are none or the call fails.
 */
function MerchantStrip({ merchants }: { merchants: ParticipatingMerchant[] }) {
  const names = merchants.map((merchant) => merchant.name);
  return (
    <div className="mt-6 border-t border-[#5CB891]/30 pt-5">
      <p
        id="give-at-stores-heading"
        className="text-xs font-semibold uppercase tracking-widest text-gray-500"
      >
        Donate at stores like these
      </p>
      <ul
        aria-labelledby="give-at-stores-heading"
        className="mt-3 flex justify-center"
      >
        {merchants.map((merchant, index) => (
          <li
            key={merchant.merchant_id}
            title={merchant.name}
            className={clsx(
              'relative h-12 w-12 overflow-hidden rounded-full border-2 border-white bg-white shadow-sm',
              index > 0 && '-ml-3'
            )}
            style={{ zIndex: merchants.length - index }}
          >
            <ImageWithFallback
              src={merchant.logo_url ?? undefined}
              alt={merchant.name}
              className="size-full object-contain p-1"
              fallback={
                <span
                  role="img"
                  aria-label={merchant.name}
                  className={clsx(
                    'flex size-full items-center justify-center text-sm font-semibold',
                    DONATION_TONE.iconTile
                  )}
                >
                  {nameInitials(merchant.name)}
                </span>
              }
            />
          </li>
        ))}
      </ul>
      {/* Touch screens have no hover, so the names are also written out. */}
      <p className="mt-2 text-xs text-gray-600" aria-hidden="true">
        {shortNameList(names)}
      </p>
    </div>
  );
}

/** My Donations before the first donation: what giving is and where it happens. */
export function DonationsEmptyState() {
  const merchants = useParticipatingMerchants().data?.slice(0, MAX_MERCHANTS);
  return (
    <>
      <section
        aria-labelledby="donations-empty-heading"
        className={clsx(
          'rounded-3xl border px-5 py-8 text-center shadow-sm',
          DONATION_TONE.card
        )}
      >
        <span
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#5CB891]/10"
          aria-hidden="true"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#5CB891]/20">
            <HeartIcon className="h-7 w-7 text-emerald-700" />
          </span>
        </span>
        <p
          className={clsx(
            'mt-5 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-widest',
            DONATION_TONE.text
          )}
        >
          <HeartOutlineIcon className="h-4 w-4" aria-hidden="true" />
          No donations yet
        </p>
        <h2
          id="donations-empty-heading"
          className="mt-2 text-2xl font-bold text-gray-900 [text-wrap:balance]"
        >
          Small change, real difference
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-600 [text-wrap:balance]">
          When you pay with AyeFace at a store that supports a charity, you can
          add a small donation at checkout, like rounding up your bill.
        </p>
        {merchants && merchants.length > 0 && (
          <MerchantStrip merchants={merchants} />
        )}
      </section>

      <section aria-labelledby="how-giving-works-heading">
        <h2
          id="how-giving-works-heading"
          className="mb-3 mt-3 text-sm font-bold uppercase tracking-wider text-gray-700"
        >
          How it works
        </h2>
        <ol className={CARD}>
          {STEPS.map((step, index) => {
            const last = index === STEPS.length - 1;
            return (
              <li
                key={step.title}
                className={clsx('relative flex gap-4', !last && 'pb-6')}
              >
                {!last && (
                  <span
                    className="absolute bottom-0 left-4 top-9 w-px -translate-x-1/2 bg-[#5CB891]/40"
                    aria-hidden="true"
                  />
                )}
                <span
                  className={clsx(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold',
                    DONATION_TONE.iconTile
                  )}
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <div className="min-w-0 pt-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {step.title}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">{step.body}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}
